import { useCallback, useMemo } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { toast } from 'sonner';
import { Calendar, Clock, X, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PostWriterSubmenuMemo } from '@/components/post-writer/PostWriterSubmenu';
import { api } from '../../convex/_generated/api';
import { Id } from '../../convex/_generated/dataModel';
import { formatScheduledTime, getTimeUntilPublish } from '@/lib/timezone';
import type { IScheduledPost } from '@/@types/scheduledPost';

const ScheduledPostsPage = () => {
  const scheduledPosts = useQuery(api.socialMedia.scheduledPublishing.listScheduledPosts, {});
  const cancelScheduledPost = useMutation(api.socialMedia.scheduledPublishing.cancelScheduledPost);

  const isLoading = scheduledPosts === undefined;
  const isEmpty = scheduledPosts?.length === 0;

  const handleCancel = useCallback(
    async (scheduledPostId: Id<'scheduledPosts'>, scheduledTime: string) => {
      const confirmed = window.confirm(
        `Are you sure you want to cancel this scheduled post for ${scheduledTime}?`
      );

      if (!confirmed) return;

      try {
        await cancelScheduledPost({ scheduledPostId });
        toast.success('Scheduled post cancelled successfully');
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Failed to cancel scheduled post');
      }
    },
    [cancelScheduledPost]
  );

  const statusConfig = useMemo(
    () => ({
      scheduled: {
        label: 'Scheduled',
        icon: Clock,
        className: 'bg-blue-100 text-blue-800 border-blue-200',
      },
      publishing: {
        label: 'Publishing',
        icon: Loader2,
        className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      },
      published: {
        label: 'Published',
        icon: CheckCircle2,
        className: 'bg-green-100 text-green-800 border-green-200',
      },
      failed: {
        label: 'Failed',
        icon: AlertCircle,
        className: 'bg-red-100 text-red-800 border-red-200',
      },
      cancelled: {
        label: 'Cancelled',
        icon: X,
        className: 'bg-gray-100 text-gray-800 border-gray-200',
      },
    }),
    []
  );

  const mainContent = (
    <div className="container mx-auto p-6 max-w-4xl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Calendar className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold">Scheduled Posts</h1>
        </div>
        <p className="text-gray-600">
          Manage your scheduled Facebook posts and view publishing history
        </p>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      )}

      {/* Empty State */}
      {!isLoading && isEmpty && (
        <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-lg">
          <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-semibold mb-2 text-gray-700">No scheduled posts</h2>
          <p className="text-gray-500 mb-6">
            Schedule your first post by clicking "Schedule Publishing" on any post
          </p>
        </div>
      )}

      {/* Scheduled Posts List */}
      {!isLoading && !isEmpty && (
        <div className="space-y-4">
          {scheduledPosts.map((scheduled: IScheduledPost) => {
            const config = statusConfig[scheduled.status];
            const StatusIcon = config.icon;
            const formattedTime = formatScheduledTime(scheduled.scheduledFor, scheduled.timezone);
            const timeUntil =
              scheduled.status === 'scheduled' ? getTimeUntilPublish(scheduled.scheduledFor) : null;

            return (
              <div
                key={scheduled._id}
                className="border rounded-lg p-6 hover:shadow-md transition-shadow bg-white"
              >
                {/* Header with Status Badge */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border ${config.className}`}
                      >
                        <StatusIcon
                          className={`w-4 h-4 ${scheduled.status === 'publishing' ? 'animate-spin' : ''}`}
                        />
                        {config.label}
                      </span>
                      {timeUntil && (
                        <span className="text-sm text-gray-500 font-medium">{timeUntil}</span>
                      )}
                    </div>
                  </div>

                  {/* Cancel Button - Only for scheduled posts */}
                  {scheduled.status === 'scheduled' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCancel(scheduled._id, formattedTime)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Cancel
                    </Button>
                  )}
                </div>

                {/* Scheduled Time */}
                <div className="flex items-center gap-2 mb-3 text-gray-700">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="font-medium">{formattedTime}</span>
                </div>

                {/* Target Accounts */}
                <div className="mb-3">
                  <p className="text-sm text-gray-600">
                    Publishing to <span className="font-medium">{scheduled.targetAccountIds.length}</span>{' '}
                    {scheduled.targetAccountIds.length === 1 ? 'account' : 'accounts'}
                  </p>
                </div>

                {/* Error Message */}
                {scheduled.errorMessage && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-red-800">Publishing failed</p>
                        <p className="text-sm text-red-700">{scheduled.errorMessage}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Published Time */}
                {scheduled.publishedAt && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-sm text-gray-600">
                      Published on{' '}
                      {new Date(scheduled.publishedAt).toLocaleString('en-GB', {
                        timeZone: scheduled.timezone,
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      })}
                    </p>
                  </div>
                )}

                {/* Cancelled Time */}
                {scheduled.cancelledAt && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-sm text-gray-600">
                      Cancelled on{' '}
                      {new Date(scheduled.cancelledAt).toLocaleString('en-GB', {
                        timeZone: scheduled.timezone,
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      })}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return {
    submenu: <PostWriterSubmenuMemo />,
    mainContent,
  };
};

export default ScheduledPostsPage;
